import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Menu, Plus, Check, Minus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAudio } from '@/hooks/useAudio';
import { addToFavorites, isTrackFavorited } from '@/lib/favoritesService';
import { listeningSessionService } from '@/lib/listeningSessionService';

const ACTIVE_LISTENING_SESSION_KEY = 'pravos_active_listening_session';

interface AudioPlaybarProps {
  albumId: string | null;
  albumTitle: string;
  albumCover: string;
  trackPath: string | string[] | null;
  trackTitle?: string;
  trackId?: string | null;
  userId?: string | null;
  isTimerPlaying: boolean;
  onPlayPause: (isPlaying: boolean) => void;
  onNextTrack: () => void;
  onPreviousTrack: () => void;
  onTrackComplete: () => void;
  onLoadError?: () => void; // Callback when track fails to load
  onOpenMenu?: () => void; // Callback to open mobile menu
  onHideTrack?: (trackId: string) => void; // Callback to hide track from all playback
}

export function AudioPlaybar({
  albumId,
  albumTitle,
  albumCover,
  trackPath,
  trackTitle,
  trackId,
  userId,
  isTimerPlaying,
  onPlayPause,
  onNextTrack,
  onPreviousTrack,
  onTrackComplete,
  onLoadError,
  onOpenMenu,
  onHideTrack,
}: AudioPlaybarProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const listeningSessionIdRef = useRef<string | null>(null);
  const listeningStartRef = useRef<number | null>(null);
  const activeTrackRef = useRef<{ trackId: string | null; albumId: string | null }>({
    trackId: null,
    albumId: null,
  });

  const endListeningSession = useCallback(
    async ({ completed = false, skipped = false }: { completed?: boolean; skipped?: boolean } = {}) => {
      if (!userId || !listeningSessionIdRef.current) {
        return;
      }

      const sessionId = listeningSessionIdRef.current;
      listeningSessionIdRef.current = null;

      const startedAt = listeningStartRef.current;
      listeningStartRef.current = null;

      localStorage.removeItem(ACTIVE_LISTENING_SESSION_KEY);

      const duration = startedAt ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0;

      try {
        await listeningSessionService.endListening({
          sessionId,
          durationSeconds: duration,
          completed,
          skipped,
        });
      } catch (err) {
        console.error('Failed to end listening session:', err);
      } finally {
        activeTrackRef.current = { trackId: null, albumId: null };
        queryClient.invalidateQueries({ queryKey: ['listening-sessions', userId] });
      }
    },
    [userId, queryClient]
  );

  const startListeningSession = useCallback(async () => {
    if (!userId || !albumId || !trackId) {
      return;
    }

    if (
      listeningSessionIdRef.current &&
      (activeTrackRef.current.trackId !== trackId || activeTrackRef.current.albumId !== albumId)
    ) {
      await endListeningSession({ skipped: true });
    } else if (listeningSessionIdRef.current) {
      return;
    }

    const startTime = Date.now();
    listeningStartRef.current = startTime;

    try {
      const { data, error } = await listeningSessionService.startListening({
        userId,
        albumId,
        trackId,
      });

      if (error || !data) {
        console.error('Failed to start listening session:', error);
        listeningStartRef.current = null;
        return;
      }

      listeningSessionIdRef.current = data.id;
      activeTrackRef.current = { trackId, albumId };
      localStorage.setItem(
        ACTIVE_LISTENING_SESSION_KEY,
        JSON.stringify({ sessionId: data.id, startTime, userId })
      );
    } catch (err) {
      console.error('Failed to start listening session:', err);
      listeningStartRef.current = null;
    }
  }, [userId, albumId, trackId, endListeningSession]);

  const handleTrackCompleteInternal = useCallback(() => {
    endListeningSession({ completed: true });
    onTrackComplete();
  }, [endListeningSession, onTrackComplete]);

  // Handle track load error - show which track failed and skip
  const handleTrackLoadError = useCallback((src: string) => {
    const filename = src.split('/').pop() || 'Unknown track';
    toast.error(`Track unavailable: ${trackTitle || filename}`, {
      description: 'Skipping to next track...',
    });
    onLoadError?.();
  }, [trackTitle, onLoadError]);

  const audio = useAudio(trackPath || '', {
    onEnd: handleTrackCompleteInternal,
    onLoadError: handleTrackLoadError,
    loop: false,
  });
  const [volume, setVolume] = useState(0.7);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isVolumeActive, setIsVolumeActive] = useState(false);
  const [isAddingFavorite, setIsAddingFavorite] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [showMenuPulse, setShowMenuPulse] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const prevTimerPlayingRef = useRef(isTimerPlaying);

  // Recover orphaned listening session after reload/crash
  useEffect(() => {
    if (!userId) {
      localStorage.removeItem(ACTIVE_LISTENING_SESSION_KEY);
      return;
    }

    const stored = localStorage.getItem(ACTIVE_LISTENING_SESSION_KEY);
    if (!stored) return;

    try {
      const parsed = JSON.parse(stored) as { sessionId?: string; startTime?: number; userId?: string };
      if (!parsed.sessionId || !parsed.startTime || parsed.userId !== userId) {
        return;
      }

      const duration = Math.max(0, Math.floor((Date.now() - parsed.startTime) / 1000));
      if (duration <= 0) {
        return;
      }

      listeningSessionService.endListening({
        sessionId: parsed.sessionId,
        durationSeconds: duration,
        completed: false,
        skipped: false,
      }).finally(() => {
        queryClient.invalidateQueries({ queryKey: ['listening-sessions', userId] });
      });
    } catch (err) {
      console.error('Failed to recover listening session:', err);
    } finally {
      localStorage.removeItem(ACTIVE_LISTENING_SESSION_KEY);
    }
  }, [userId, queryClient]);

  // Consolidated audio playback sync - single source of truth
  // Handles: timer state changes, track changes, and late-loading tracks
  const prevTrackPathRef = useRef(trackPath);
  const prevIsLoadedRef = useRef(audio.isLoaded);

  useEffect(() => {
    const timerStateChanged = prevTimerPlayingRef.current !== isTimerPlaying;
    const trackChanged = prevTrackPathRef.current !== trackPath;
    const justLoaded = !prevIsLoadedRef.current && audio.isLoaded;

    // Update refs
    prevTimerPlayingRef.current = isTimerPlaying;
    prevTrackPathRef.current = trackPath;
    prevIsLoadedRef.current = audio.isLoaded;

    // Priority 1: Timer state change (user-initiated play/pause)
    if (timerStateChanged) {
      if (isTimerPlaying) {
        audio.play();
      } else {
        audio.pause();
      }
      return; // Don't process other conditions on timer state change
    }

    // Priority 2: Track changed while timer is playing - auto-play new track
    if (trackChanged && audio.isLoaded && isTimerPlaying) {
      audio.play();
      return;
    }

    // Priority 3: Track just loaded while timer is playing but audio isn't
    if (justLoaded && isTimerPlaying && !audio.isPlaying) {
      audio.play();
    }
  }, [isTimerPlaying, trackPath, audio.isLoaded, audio.isPlaying, audio.play, audio.pause]);

  useEffect(() => {
    if (!userId) {
      endListeningSession();
      return;
    }

    if (isTimerPlaying && albumId && trackId) {
      startListeningSession();
    } else {
      endListeningSession();
    }
  }, [isTimerPlaying, albumId, trackId, userId, startListeningSession, endListeningSession]);

  // Load saved volume on mount only
  useEffect(() => {
    const savedVolume = localStorage.getItem('audioVolume');
    if (savedVolume) {
      const vol = parseFloat(savedVolume);
      setVolume(vol);
      audio.setVolume(vol);
    } else {
      // Set initial volume if none saved
      audio.setVolume(0.7);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Listen for volume changes from mobile menu and keyboard shortcuts
  useEffect(() => {
    const handleVolumeChange = (e: Event) => {
      const customEvent = e as CustomEvent<number>;
      const newVolume = customEvent.detail;
      setVolume(newVolume);
      audio.setVolume(newVolume);
    };

    window.addEventListener('volumeChange', handleVolumeChange);
    return () => window.removeEventListener('volumeChange', handleVolumeChange);
  }, [audio]);

  // Check if track is favorited on mount and when trackId changes
  useEffect(() => {
    if (!trackId || !userId) {
      setIsFavorited(false);
      return;
    }

    async function checkFavoriteStatus() {
      // Type guard: both trackId and userId are guaranteed to be strings here
      const { isFavorited: favorited } = await isTrackFavorited(userId as string, trackId as string);
      setIsFavorited(favorited);
    }

    // Reset state immediately when track changes, then check
    setIsFavorited(false);
    checkFavoriteStatus();
  }, [trackId, userId]);

  // Show subtle pulse animation on first visit to help users discover mobile menu
  useEffect(() => {
    const hasSeenMenuHint = localStorage.getItem('pravos-menu-hint-seen');
    if (!hasSeenMenuHint) {
      // Delay to let user settle in first
      const timer = setTimeout(() => {
        setShowMenuPulse(true);
        // Stop pulsing after 5 seconds
        setTimeout(() => {
          setShowMenuPulse(false);
          localStorage.setItem('pravos-menu-hint-seen', 'true');
        }, 5000);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    return () => {
      endListeningSession();
    };
  }, [endListeningSession]);

  const handleNavigateToMusic = useCallback(() => {
    navigate('/app/music');
  }, [navigate]);

  const handlePlayPause = useCallback(() => {
    // Toggle play/pause and notify parent (timer)
    onPlayPause(!isTimerPlaying);
  }, [isTimerPlaying, onPlayPause]);

  const handleNextTrackClick = useCallback(() => {
    endListeningSession({ skipped: true });
    onNextTrack();
  }, [endListeningSession, onNextTrack]);

  const handlePreviousTrackClick = useCallback(() => {
    endListeningSession({ skipped: true });
    onPreviousTrack();
  }, [endListeningSession, onPreviousTrack]);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    audio.setVolume(newVolume);
  }, [audio]);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current || !audio.duration) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * audio.duration;

    audio.seek(newTime);
  }, [audio]);

  const handleProgressMouseDown = useCallback(() => {
    setIsSeeking(true);
  }, []);

  const handleProgressMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSeeking || !progressBarRef.current || !audio.duration) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * audio.duration;

    audio.seek(newTime);
  }, [isSeeking, audio]);

  const handleProgressMouseUp = useCallback(() => {
    setIsSeeking(false);
  }, []);

  const handleAddToFavorites = useCallback(async () => {
    if (!trackId || !userId) {
      return;
    }

    if (isFavorited) {
      return;
    }

    setIsAddingFavorite(true);
    try {
      const { error } = await addToFavorites(userId, trackId);
      if (error) {
        console.error('Failed to add to favorites:', error);
      } else {
        setIsFavorited(true);

        // Emit event to notify other components (like MusicPlaylistSelector)
        window.dispatchEvent(new CustomEvent('favoriteAdded', {
          detail: { trackId, userId }
        }));
      }
    } catch (err) {
      console.error('Failed to add to favorites:', err);
    } finally {
      setIsAddingFavorite(false);
    }
  }, [trackId, userId, isFavorited]);

  // Listen for keyboard shortcut to add to favorites (F key)
  useEffect(() => {
    const handleAddToFavoritesKeyboard = () => {
      handleAddToFavorites();
    };

    window.addEventListener('addToFavoritesKeyboard', handleAddToFavoritesKeyboard);
    return () => window.removeEventListener('addToFavoritesKeyboard', handleAddToFavoritesKeyboard);
  }, [handleAddToFavorites]);

  const handleHideTrack = useCallback(() => {
    if (!trackId || !onHideTrack) {
      return;
    }

    onHideTrack(trackId);
    toast.success('Track hidden from focus', {
      description: 'Manage hidden tracks in Settings',
    });
  }, [trackId, onHideTrack]);

  const progress = audio.getProgress();

  return (
    <div className="fixed bottom-0 left-0 right-0 h-[90px] bg-glass-dark border-t border-glass-border backdrop-blur-md z-30">
      {/* Main Content Grid */}
      <div className="grid grid-cols-3 items-center h-full px-4 md:px-6 lg:px-8">
        {/* Left: Track Info */}
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          {/* Album Cover */}
          <button
            onClick={handleNavigateToMusic}
            className="w-[48px] h-[48px] md:w-[52px] md:h-[52px] rounded-[10px] overflow-hidden flex-shrink-0 bg-gradient-to-br from-brand-purple/30 to-brand-pink/30 cursor-pointer hover:opacity-80 transition-opacity"
            title="Open Music"
            aria-label="Open Music"
          >
            {albumCover ? (
              <img
                src={albumCover}
                alt={albumTitle}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Play className="w-6 h-6 text-white/50" />
              </div>
            )}
          </button>

          {/* Track Details */}
          <button
            onClick={handleNavigateToMusic}
            className="min-w-0 overflow-hidden text-left cursor-pointer hover:opacity-80 transition-opacity"
            title="Open Music"
            aria-label="Open Music"
          >
            <h4 className="font-sans text-[14px] md:text-[15px] leading-[18px] text-text-primary truncate">
              {trackTitle || albumTitle || 'Select a playlist'}
            </h4>
            <p className="font-sans text-[12px] md:text-[13px] leading-[16px] text-text-secondary truncate mt-0.5">
              {albumTitle || 'Choose music to begin'}
            </p>
          </button>

          {/* Track Actions (Favorites + Hide) */}
          {trackId && userId && (
            <div className="flex flex-col gap-1.5">
              {/* Add to Favorites Button */}
              <button
                onClick={handleAddToFavorites}
                disabled={isAddingFavorite}
                className={`flex-shrink-0 w-[18px] h-[18px] md:w-5 md:h-5 rounded-full flex items-center justify-center transition-all group relative ${
                  isFavorited
                    ? 'border-2 border-success-green bg-glass-dark opacity-80'
                    : 'border-2 border-glass-border bg-glass-dark text-text-secondary hover:text-text-primary hover:border-text-primary opacity-80'
                }`}
                title={isFavorited ? 'Added to favorites' : 'Add to favorites'}
                aria-label={isFavorited ? 'Added to favorites' : 'Add to favorites'}
              >
                {isFavorited ? (
                  <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-success-green" strokeWidth={2.5} />
                ) : (
                  <Plus className="w-2.5 h-2.5 md:w-3 md:h-3 transition-colors" strokeWidth={2.5} />
                )}
                {/* Tooltip */}
                {!isFavorited && (
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-[11px] font-sans text-white bg-black/80 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                    Add to favorites
                  </span>
                )}
              </button>

              {/* Hide Track Button */}
              <button
                onClick={handleHideTrack}
                className="flex-shrink-0 w-[18px] h-[18px] md:w-5 md:h-5 rounded-full flex items-center justify-center transition-all group relative border-2 border-glass-border bg-glass-dark text-text-secondary hover:text-error-pink hover:border-error-pink opacity-80"
                title="Hide from focus"
                aria-label="Hide track from playback"
              >
                <Minus className="w-2.5 h-2.5 md:w-3 md:h-3 transition-colors" strokeWidth={2.5} />
                {/* Tooltip */}
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-[11px] font-sans text-white bg-black/80 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  Hide from focus
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Center: Playback Controls + Progress Bar */}
        <div className="flex flex-col items-center justify-center gap-[7px]">
          {/* Playback Controls Row */}
          <div className="flex items-center justify-center gap-2 md:gap-3">
            {/* Previous Track */}
            <button
              onClick={handlePreviousTrackClick}
              className="p-1.5 rounded-full text-text-secondary hover:text-text-primary transition-colors"
              title="Previous Track"
            >
              <SkipBack className="w-[18px] h-[18px] md:w-[19px] md:h-[19px]" />
            </button>

            {/* Play/Pause Button - Primary Style */}
            <button
              onClick={handlePlayPause}
              disabled={!albumId}
              className="w-[37px] h-[37px] md:w-[41px] md:h-[41px] rounded-full bg-gradient-to-r from-[#C98AA6] to-[#8B6FCB] hover:shadow-glow-md transition-all flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed shadow-[0px_0px_38px_0px_rgba(139,111,203,0.3)] shadow-[inset_0px_0px_8px_0px_rgba(11,10,16,0.6)]"
              title={isTimerPlaying ? 'Pause' : 'Play'}
            >
              {isTimerPlaying ? (
                <Pause className="w-[20px] h-[20px] md:w-[22px] md:h-[22px] text-white" />
              ) : (
                <Play className="w-[20px] h-[20px] md:w-[22px] md:h-[22px] text-white ml-0.5" />
              )}
            </button>

            {/* Next Track */}
            <button
              onClick={handleNextTrackClick}
              className="p-1.5 rounded-full text-text-secondary hover:text-text-primary transition-colors"
              title="Next Track"
            >
              <SkipForward className="w-[18px] h-[18px] md:w-[19px] md:h-[19px]" />
            </button>
          </div>

          {/* Progress Bar + Time Display Row */}
          <div className="flex items-center gap-2 md:gap-3 w-full max-w-[600px]">
            {/* Start Time */}
            <span className="font-mono text-[11px] md:text-[12px] text-text-secondary tracking-[-1px] flex-shrink-0">
              {audio.formatTime(audio.currentTime)}
            </span>

            {/* Progress Bar */}
            <div
              ref={progressBarRef}
              className="relative h-1 bg-white/10 rounded-full cursor-pointer group flex-1"
              onClick={handleProgressClick}
              onMouseDown={handleProgressMouseDown}
              onMouseMove={handleProgressMouseMove}
              onMouseUp={handleProgressMouseUp}
              onMouseLeave={handleProgressMouseUp}
            >
              {/* Progress Fill with Gradient */}
              <div
                className="h-full bg-gradient-to-r from-[#C98AA6] to-[#8B6FCB] rounded-full transition-all duration-100 relative"
                style={{ width: `${progress * 100}%` }}
              >
                {/* Scrubber Dot */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" />
              </div>
            </div>

            {/* End Time */}
            <span className="font-mono text-[11px] md:text-[12px] text-text-secondary tracking-[-1px] flex-shrink-0">
              {audio.formatTime(audio.duration)}
            </span>
          </div>
        </div>

        {/* Right: Volume Control (Desktop) / Menu Button (Mobile) */}
        <div className="flex items-center justify-end gap-2">
          {/* Mobile Only: Menu Button */}
          {onOpenMenu && (
            <button
              onClick={onOpenMenu}
              className={`nav:hidden flex items-center gap-2 group relative ${
                showMenuPulse ? 'animate-pulse' : ''
              }`}
              aria-label="Open Menu"
            >
              <span className="font-sans text-body tracking-[0.08em] uppercase text-text-secondary group-hover:text-text-primary transition-colors">
                Menu
              </span>
              <Menu className="w-6 h-6 text-text-secondary group-hover:text-text-primary transition-colors" />
              
              {/* Subtle pulse ring to draw attention on first visit */}
              {showMenuPulse && (
                <span className="absolute inset-0 rounded-lg border-2 border-text-primary/30 animate-ping" />
              )}
            </button>
          )}

          {/* Desktop Only: Volume Control */}
          <button
            onClick={() => {
              const newVolume = volume > 0 ? 0 : 0.7;
              setVolume(newVolume);
              audio.setVolume(newVolume);
            }}
            className="hidden nav:block p-2 text-text-secondary hover:text-text-primary transition-colors"
          >
            {volume === 0 ? (
              <VolumeX className="w-5 h-5" />
            ) : (
              <Volume2 className="w-5 h-5" />
            )}
          </button>

          {/* Desktop Only: Volume Slider */}
          <div
            className="hidden nav:block relative w-[120px] h-1 group"
            onMouseEnter={() => setIsVolumeActive(true)}
            onMouseLeave={() => setIsVolumeActive(false)}
          >
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              onMouseDown={() => setIsVolumeActive(true)}
              onMouseUp={() => setIsVolumeActive(false)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            />
            {/* Track Background */}
            <div className="absolute inset-0 bg-white/10 rounded-full" />
            {/* Volume Fill - Gradient when active, off-white otherwise */}
            <div
              className={`absolute left-0 top-0 h-full rounded-full transition-all duration-100 ${
                isVolumeActive
                  ? 'bg-gradient-to-r from-[#C98AA6] to-[#8B6FCB]'
                  : 'bg-text-secondary/60'
              }`}
              style={{ width: `${volume * 100}%` }}
            />
            {/* Volume Scrubber Dot */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${volume * 100}% - 6px)` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
